import React from 'react';
import { i18n } from 'config/locales';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonScrollIntoView,
  CommonRequired,
  CommonAddressBooks,
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import { ForeignActivitiesSupportItemModel } from 'forms/sections/foreign/activities/support/types';
import {
  CountryArrayModel, CountryModel, LocationModel
} from 'forms/shared/types';
import { Dispatch } from 'redux';
import Field from 'components/Form/Field';
import Name from 'components/Form/Name';
import Textarea from 'components/Form/Textarea';
import Currency from 'components/Form/Currency';
import Checkbox from 'components/Form/Checkbox';
import Country from 'components/Form/Country';
import Location from 'components/Form/Location';
import { isPVQForm } from 'helpers/form';
import Text from 'components/Form/Text';
import PVQLocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import Branch from 'components/Form/Branch';
import PVQName from 'components/Form/PVQName';
import { isSafeInternational } from 'helpers/locations';
import Dropdown from 'components/Form/Dropdown';
import { foreignContactRelationshipOptions } from 'constants/enums/foreignActivityOptions';

interface Props extends Partial<ForeignActivitiesSupportItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
   formType?: string

  dispatch: Dispatch
}

export default class SupportItem extends ValidationElement<Props> {
  handleNameError = (value: any, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code:
          err.code.includes('required')
            ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
            : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  update = (queue: Partial<ForeignActivitiesSupportItemModel>) => {
    this.props.onUpdate({
      Name: this.props.Name,
      Address: this.props.Address,
      Relationship: this.props.Relationship,
      Amount: this.props.Amount,
      AmountEstimated: this.props.AmountEstimated,
      Frequency: this.props.Frequency,
      Citizenship: this.props.Citizenship,
      // pvq
      PlansForFutureSupportExplain: this.props.PlansForFutureSupportExplain,
      PlansForFutureSupport: this.props.PlansForFutureSupport,
      ReasonForSupport: this.props.ReasonForSupport,
      AddressNotApplicable: this.props.AddressNotApplicable,
      RelationshipType: this.props.RelationshipType,
      ...queue,
    });
  };

  updateName = (values: ForeignActivitiesSupportItemModel['Name']) => {
    this.update({
      Name: values,
    });
  };

  updateAddress = (values: ForeignActivitiesSupportItemModel['Address']) => {
    this.update({
      Address: values,
    });
  };

  updateAddressNotApplicable = (
    addressNotApplicable: ForeignActivitiesSupportItemModel['AddressNotApplicable']
  ) => {
    const queue: Partial<ForeignActivitiesSupportItemModel> = { AddressNotApplicable: addressNotApplicable };

    if (addressNotApplicable?.applicable === false) {
      const address: LocationModel | undefined = this.props.Address;

      let country: CountryModel | string | CountryArrayModel | undefined = address?.country;

      if (country) {
        if (isSafeInternational(address)) {
          country = { value: 'INTERNATIONAL' } as CountryModel;
        }

        queue.Address = { country } as LocationModel;
      } else {
        queue.Address = { } as LocationModel;
      }
    }

    this.update(queue);
  };

  updateRelationship = (values: ForeignActivitiesSupportItemModel['Relationship']) => {
    this.update({
      Relationship: values,
    });
  };

  updateAmount = (values: ForeignActivitiesSupportItemModel['Amount']) => {
    this.update({
      Amount: values,
    });
  };

  updateAmountEstimated = (values: ForeignActivitiesSupportItemModel['AmountEstimated']) => {
    this.update({
      AmountEstimated: values,
    });
  };

  updateFrequency = (values: ForeignActivitiesSupportItemModel['Frequency']) => {
    this.update({
      Frequency: values,
    });
  };

  updateCitizenship = (values: ForeignActivitiesSupportItemModel['Citizenship']) => {
    this.update({
      Citizenship: values,
    });
  };

  updatePlansForFutureSupportExplain = (values: ForeignActivitiesSupportItemModel['PlansForFutureSupportExplain']) => {
    this.update({
      PlansForFutureSupportExplain: values,
    });
  };

  updatePlansForFutureSupport = (values: ForeignActivitiesSupportItemModel['PlansForFutureSupport']) => {
    this.update({
      PlansForFutureSupport: values,
    });
  };

  updateReasonForSupport = (values: ForeignActivitiesSupportItemModel['ReasonForSupport']) => {
    this.update({
      ReasonForSupport: values,
    });
  };

  updateRelationshipType = (values: ForeignActivitiesSupportItemModel['RelationshipType']) => {
    this.update({
      RelationshipType: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;

    const isPVQ = isPVQForm(this.props.formType);
    const prefixLocale = `foreign.pvq.activities.support.branch`;

    return isPVQ ? (
      <div className="support-item">
        <Field
          title={i18n.t(`${prefixLocale}.name`)}
          attribute={`${attribute}.Name`}
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
        >
          <PVQName
            {...this.props.Name}
            name="Name"
            onUpdate={this.updateName}
            onError={this.handleNameError}
            className="advice-name"
            required={this.props.required}
            attribute={`${attribute}.Name`}
            showLastFirstMiddleNameOrder

          />
        </Field>

        <Field
          optional
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Address`}
        >
          <PVQLocationUnknown
            {...this.props.Address}
            addressUnknown={this.props.AddressNotApplicable}
            addressUnknownLabel={i18n.t('foreign.pvq.contacts.label.idk')}
            attribute={`${attribute}.Address`}
            name="foreign-national-address"
            onUpdate={this.updateAddress} // eslint-disable-line @typescript-eslint/unbound-method
            onUpdateAddressUnknown={this.updateAddressNotApplicable}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            onError={this.props.onError}
            required={this.props.required}
            geocode
            showAddressUnknown
            showCityUnknown
            showInternationalStreet={false}
            showIsInstallationUnknownIfDomestic
            showIsInstallationUnknownIfInternational
            showMilitaryInstallation
            hideInstallationNameUnknownWhenForeign
            showApoFpoUnknown
            showStreetUnknown
            showZipcodeUnknown
            label={i18n.t(`foreign.pvq.activities.financialSupportInquiry.residenceInUS.label.question`)}
            domesticTitle={i18n.t(`foreign.pvq.contacts.heading.addressDomesticTitle`)}
            foreignTitle={i18n.t(`foreign.pvq.contacts.heading.addressForeignTitle`)}
          />
        </Field>

        <Field
          attribute={`${attribute}.RelationshipType`}
          title={i18n.t(`${prefixLocale}.relationship`)}
        >
          <Dropdown
            name="RelationshipType"
            {...this.props.RelationshipType}
            onUpdate={this.updateRelationshipType}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.RelationshipType`}
            value={this.props.RelationshipType?.value}
          >
            {foreignContactRelationshipOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Dropdown>

        </Field>
        {/* explanation field when other relationship type is selected */}
        {this.props.RelationshipType?.value === 'Other' && (
          <Field
            title={i18n.t('relationships.pvq.people.person.heading.contactFrequencyOther')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Relationship`}
          >

            <Textarea
              name="Relationship"
              {...this.props.Relationship}
              onUpdate={this.updateRelationship}
              attribute={`${attribute}.Relationship`}
              onError={this.props.onError}
              required={this.props.required}
            />

          </Field>
        )}

        <Field
          attribute={`${attribute}.Frequency`}
          title={i18n.t(`${prefixLocale}.frequency`)}
        >
          <Textarea
            name="Frequency"
            {...this.props.Frequency}
            onUpdate={this.updateFrequency}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Frequency`}
          />
        </Field>

        <Field
          attribute={`${attribute}.Amount`}
          title={i18n.t(`${prefixLocale}.total`)}
        >
          <Textarea
            name="Amount"
            {...this.props.Amount}
            onUpdate={this.updateAmount}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Amount`}
          />
        </Field>

        <Field
          attribute={`${attribute}.ReasonForSupport`}
          title={i18n.t(`${prefixLocale}.why`)}
        >
          <Textarea
            name="ReasonForSupport"
            {...this.props.ReasonForSupport}
            onUpdate={this.updateReasonForSupport}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.ReasonForSupport`}
          />
        </Field>

        <Field
          title={i18n.t(`${prefixLocale}.plan`)}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.PlansForFutureSupport`}
        >
          <Branch
            name="PlansForFutureSupport"
            attribute={`${attribute}.PlansForFutureSupport`}
            labelSize="h4"
            className="PlansForFutureSupport no-margin-bottom"
            {...this.props.PlansForFutureSupport}
            onUpdate={this.updatePlansForFutureSupport}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>
        <Field
          title={i18n.t(`${prefixLocale}.explain`)}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.PlansForFutureSupportExplain`}
        >
          <Textarea
            name="PlansForFutureSupportExplain"
            className="PlansForFutureSupportExplain"
            {...this.props.PlansForFutureSupportExplain}
            onUpdate={this.updatePlansForFutureSupportExplain}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.PlansForFutureSupportExplain`}
          />

        </Field>

        <Field
          title={i18n.t(`${prefixLocale}.countries`)}
          adjustFor="country"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Citizenship`}
        >
          <Country
            name="Citizenship"
            {...this.props.Citizenship}
            className="foreign-citizenship"
            multiple
            required={this.props.required}
            onUpdate={this.updateCitizenship}
            onError={this.props.onError}
            attribute={`${attribute}.Citizenship`}
          />
        </Field>
      </div>
    ) : (

      <div className="support-item">
        <Field
          title={i18n.t('foreign.activities.support.heading.name')}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Name
            name="Name"
            {...this.props.Name}
            className="foreign-activities-support-name"
            onUpdate={this.updateName}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            onError={this.handleNameError}
            attribute={`${attribute}.Name`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.activities.support.heading.address')}
          optional
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Address`}
        >
          <Location
            name="Address"
            {...this.props.Address}
            onUpdate={this.updateAddress}
            className="foreign-activities-support-address"
            layout={Location.ADDRESS}
            geocode
            addressBook="ForeignNational"
            showPostOffice
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.Address`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.activities.support.heading.relationship')}
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Relationship`}
        >
          <Textarea
            name="Relationship"
            {...this.props.Relationship}
            onUpdate={this.updateRelationship}
            className="foreign-activities-support-relationship"
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.Relationship`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.activities.support.heading.amount')}
          help="foreign.activities.support.help.amount"
          adjustFor="currency"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Amount`}
        >
          <Currency
            name="Amount"
            {...this.props.Amount}
            className="foreign-activities-support-amount"
            onUpdate={this.updateAmount}
            min="0"
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.Amount`}
          />
          <div className="flags">
            <Checkbox
              name="AmountEstimated"
              className="foreign-activities-support-amount-estimated"
              label={i18n.t('foreign.activities.support.label.estimated')}
              toggle="false"
              {...this.props.AmountEstimated}
              onUpdate={this.updateAmountEstimated}
              onError={this.props.onError}
              attribute={`${attribute}.AmountEstimated`}
            />
          </div>
        </Field>

        <Field
          title={i18n.t('foreign.activities.support.heading.frequency')}
          adjustFor="text"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Frequency`}
        >
          <Text
            name="Frequency"
            {...this.props.Frequency}
            className="foreign-activities-support-frequency"
            required={this.props.required}
            onUpdate={this.updateFrequency}
            onError={this.props.onError}
            attribute={`${attribute}.Frequency`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.activities.support.heading.citizenship')}
          help="foreign.activities.support.help.citizenship"
          adjustFor="country"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Citizenship`}
        >
          <Country
            name="Citizenship"
            {...this.props.Citizenship}
            className="foreign-activities-support-citizenship"
            multiple
            required={this.props.required}
            onUpdate={this.updateCitizenship}
            onError={this.props.onError}
            attribute={`${attribute}.Citizenship`}
          />
        </Field>
      </div>
    );
  }
}

SupportItem.defaultProps = {
  onUpdate: (queue) => {},
  onError: (value, arr: ErrorCode) => arr,
  PlansForFutureSupportExplain: {},
  PlansForFutureSuppor: {},
  ReasonForSupport: {},
  AddressNotApplicable: { applicable: true },
  RelationshipType: {},
};
